import React, { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { X, Search, Send, Loader2, BadgeCheck, Check, Link as LinkIcon, Facebook } from "lucide-react";
import { toast } from "sonner";

/**
 * ShareToFollowersModal — sends a debate/thought reference as a direct message
 * to one or more of the current user's followers (and following, since we allow
 * sharing with any mutual too).
 *
 * Props:
 *   open       : boolean
 *   onClose    : () => void
 *   shareRef   : { type: "debate" | "thought", id: string, title?: string, thumbnail_url?: string }
 */
export default function ShareToFollowersModal({ open, onClose, shareRef, shareUrl }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("followers"); // followers | following
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !user?.username) return;
    setLoading(true);
    Promise.all([
      api.get(`/users/${user.username}/followers`).then((r) => r.data).catch(() => []),
      api.get(`/users/${user.username}/following`).then((r) => r.data).catch(() => []),
    ])
      .then(([f1, f2]) => { setFollowers(f1); setFollowing(f2); })
      .finally(() => setLoading(false));
  }, [open, user?.username]);

  // Reset transient state whenever the modal opens
  useEffect(() => {
    if (open) { setSelected(new Set()); setNote(""); setQuery(""); setTab("followers"); }
  }, [open]);

  const source = tab === "followers" ? followers : following;
  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return source;
    return source.filter((u) =>
      u.username?.toLowerCase().includes(q) || u.full_name?.toLowerCase().includes(q)
    );
  }, [source, query]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (!shareRef || selected.size === 0) { toast.error("Pick at least one person"); return; }
    setSending(true);
    let ok = 0, fail = 0;
    for (const uid of selected) {
      try {
        await api.post(`/chats/with/${uid}/message`, { text: note || "", share_ref: { type: shareRef.type, id: shareRef.id } });
        ok++;
      } catch { fail++; }
    }
    setSending(false);
    if (ok) toast.success(`Sent to ${ok} ${ok === 1 ? "person" : "people"}`);
    if (fail) toast.error(`${fail} failed to send`);
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="share-modal-backdrop" onClick={onClose}>
      <div
        className="w-full sm:max-w-md bg-card border border-border rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-xl animate-in slide-in-from-bottom"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <div className="label-mono text-muted-foreground text-[10px]">Share via DM</div>
            <h3 className="font-serif text-xl leading-tight">Send to followers</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-muted" data-testid="share-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Post preview */}
        {shareRef?.title && (
          <div className="mx-5 mt-4 flex items-center gap-3 border border-border rounded-xl p-3 bg-muted/40">
            {shareRef.thumbnail_url ? (
              <img src={fileUrl(shareRef.thumbnail_url)} alt="" className="w-12 h-12 rounded-lg object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-[hsl(var(--primary-soft))]" />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[10px] label-mono text-muted-foreground uppercase">{shareRef.type}</div>
              <div className="text-sm font-semibold truncate">{shareRef.title}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="px-5 pt-4 flex gap-2">
          {[
            ["followers", `Followers (${followers.length})`],
            ["following", `Following (${following.length})`],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${tab === k ? "bg-[hsl(var(--primary))] text-white" : "bg-muted text-muted-foreground hover:bg-secondary"}`}
              data-testid={`share-tab-${k}`}
            >{l}</button>
          ))}
        </div>

        {/* Search */}
        <div className="px-5 pt-3">
          <div className="flex items-center gap-2 border border-border rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or @username"
              className="flex-1 bg-transparent outline-none text-sm"
              data-testid="share-search"
            />
          </div>
        </div>

        {/* List */}
        <div className="mt-3 max-h-[38vh] overflow-y-auto px-2">
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : list.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {tab === "followers" ? "No followers yet. Try the Following tab." : "You're not following anyone yet."}
            </div>
          ) : (
            list.map((u) => {
              const checked = selected.has(u.id);
              return (
                <button
                  key={u.id}
                  onClick={() => toggle(u.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-colors text-left ${checked ? "bg-[hsl(var(--primary-soft))]" : "hover:bg-muted"}`}
                  data-testid={`share-pick-${u.username}`}
                >
                  <div className="w-10 h-10 rounded-full bg-muted overflow-hidden flex items-center justify-center">
                    {u.profile_photo ? (
                      <img src={fileUrl(u.profile_photo)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-sm font-bold text-[hsl(var(--primary))]">{u.username?.[0]?.toUpperCase()}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-1">
                      @{u.username}
                      {u.verification_status === "verified" && (
                        <BadgeCheck className="w-3.5 h-3.5 fill-[hsl(var(--primary))] text-white" />
                      )}
                    </div>
                    {u.full_name && (
                      <div className="text-xs text-muted-foreground truncate">{u.full_name}</div>
                    )}
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${checked ? "bg-[hsl(var(--primary))] border-[hsl(var(--primary))]" : "border-border"}`}>
                    {checked && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Note + Send */}
        <div className="px-5 py-4 border-t border-border space-y-3">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note (optional)"
            className="w-full border border-border rounded-xl px-3 py-2 text-sm bg-transparent outline-none focus:border-[hsl(var(--primary))]"
            data-testid="share-note"
          />
          <button
            onClick={submit}
            disabled={sending || selected.size === 0}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-[hsl(var(--primary))] text-white font-semibold py-2.5 disabled:opacity-50 transition-opacity"
            data-testid="share-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? "Sending…" : selected.size ? `Send to ${selected.size}` : "Pick someone"}
          </button>

          {/* External share shortcuts */}
          {shareUrl && (
            <div className="pt-1">
              <div className="text-[10px] label-mono text-muted-foreground text-center uppercase tracking-wider mb-2">Or share elsewhere</div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(shareUrl);
                    toast.success("Link copied");
                  }}
                  className="flex flex-col items-center gap-1.5 rounded-xl border border-border py-3 hover:bg-muted transition-colors"
                  data-testid="share-copy-link"
                  aria-label="Copy link"
                >
                  <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                    <LinkIcon className="w-4 h-4 text-foreground" />
                  </div>
                  <span className="text-[11px] font-medium">Copy link</span>
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`${shareRef?.title ? shareRef.title + " — " : ""}${shareUrl}`)}`}
                  target="_blank" rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex flex-col items-center gap-1.5 rounded-xl border border-border py-3 hover:bg-muted transition-colors"
                  data-testid="share-whatsapp"
                  aria-label="Share on WhatsApp"
                >
                  <div className="w-9 h-9 rounded-full bg-[#25D366]/10 flex items-center justify-center">
                    {/* WhatsApp glyph */}
                    <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                      <path fill="#25D366" d="M20.52 3.48A11.79 11.79 0 0 0 12 0C5.37 0 .05 5.32.05 11.95c0 2.1.55 4.15 1.6 5.96L0 24l6.24-1.63a11.9 11.9 0 0 0 5.76 1.47h.01c6.62 0 11.95-5.32 11.95-11.95 0-3.18-1.24-6.17-3.44-8.41ZM12.01 21.3h-.01a9.36 9.36 0 0 1-4.77-1.3l-.34-.2-3.7.97.99-3.6-.22-.37a9.36 9.36 0 0 1-1.44-4.85c0-5.19 4.22-9.42 9.5-9.42a9.4 9.4 0 0 1 6.68 2.77 9.4 9.4 0 0 1 2.77 6.66c0 5.19-4.23 9.34-9.46 9.34Zm5.16-7c-.28-.14-1.67-.83-1.93-.92-.26-.1-.44-.14-.63.14-.19.28-.72.92-.88 1.1-.16.19-.32.21-.6.07-.28-.14-1.18-.44-2.25-1.4-.83-.74-1.4-1.65-1.57-1.93-.16-.28-.02-.43.13-.57.13-.13.28-.32.42-.48.14-.16.19-.28.28-.47.09-.19.05-.35-.02-.49-.07-.14-.63-1.52-.86-2.08-.23-.55-.46-.48-.63-.49l-.54-.01c-.19 0-.49.07-.75.35-.26.28-.98.96-.98 2.35 0 1.39 1 2.73 1.14 2.92.14.19 1.98 3.02 4.79 4.24.67.29 1.19.46 1.6.59.67.21 1.28.18 1.77.11.54-.08 1.67-.68 1.9-1.34.23-.66.23-1.22.16-1.34-.07-.11-.26-.19-.54-.33Z"/>
                    </svg>
                  </div>
                  <span className="text-[11px] font-medium">WhatsApp</span>
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank" rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex flex-col items-center gap-1.5 rounded-xl border border-border py-3 hover:bg-muted transition-colors"
                  data-testid="share-facebook"
                  aria-label="Share on Facebook"
                >
                  <div className="w-9 h-9 rounded-full bg-[#1877F2]/10 flex items-center justify-center">
                    <Facebook className="w-5 h-5 fill-[#1877F2] text-[#1877F2]" />
                  </div>
                  <span className="text-[11px] font-medium">Facebook</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
